import { Link2 } from "lucide-react";
import React from "react";
import { LinkPreview } from "@/components/ui/link-preview";

// Komponent podglądu
export const LinkPreviewComponent = () => (
  <div className="flex items-center justify-center p-8">
    <LinkPreview
      url="https://example.com"
      imageSrc="/placeholder.jpg"
      isStatic={true}
    >
      <span className="text-blue-500 underline">Hover over this link</span>
    </LinkPreview>
  </div>
);

const component = {
  id: "link-preview",
  title: "Link Preview",
  component: LinkPreview,
  description:
    "A component that shows a preview of a link when hovering over it.",
  preview: LinkPreviewComponent,
  icon: <Link2 className="h-4 w-4 mr-2" />,
  code: `import { LinkPreview } from "@/components/ui/link-preview";

export const LinkPreviewExample = () => {
  return (
    <LinkPreview 
      url="https://example.com" 
      imageSrc="/placeholder.jpg" 
      isStatic={true}
    >
      <span className="text-blue-500 underline">Hover over this link</span>
    </LinkPreview>
  );
};`,
  installation: "This component is already included in the project.",
  detailedInstallation: `
### Installation Steps

1. This component is part of the core UI library in the project.
2. If you're using this in a new project, copy the following files:
   - \`src/components/ui/link-preview.jsx\`
   - Make sure you have \`framer-motion\` and \`@radix-ui/react-hover-card\` installed in your dependencies

### Dependencies

\`\`\`json
{
  "dependencies": {
    "framer-motion": "^10.12.16",
    "@radix-ui/react-hover-card": "^1.0.0",
    "qss": "^2.0.3",
    "react": "^18.2.0",
    "react-dom": "^18.2.0",
    "react-router-dom": "^6.10.0"
  }
}
\`\`\`
`,
  sourceCode: `"use client";
import * as HoverCardPrimitive from "@radix-ui/react-hover-card";
import React, { useEffect, useState } from "react";
import { encode } from "qss";
import {
  AnimatePresence,
  motion,
  useMotionValue,
  useSpring,
} from "framer-motion";
import { Link } from "react-router-dom";

export const LinkPreview = ({
  children,
  url,
  className,
  width = 300,
  height = 225,
  quality = 50,
  isStatic = false,
  imageSrc = "",
}) => {
  let src;
  if (!isStatic) {
    const params = encode({
      url,
      screenshot: true,
      meta: false,
      embed: "screenshot.url",
      colorScheme: "dark",
      "viewport.isMobile": true,
      "viewport.deviceScaleFactor": 1,
      "viewport.width": width * 3,
      "viewport.height": height * 3,
    });
    src = \`https://api.microlink.io/?\${params}\`;
  } else {
    src = imageSrc;
  }

  const [isOpen, setOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const springConfig = { stiffness: 100, damping: 15 };
  const x = useMotionValue(0);
  const translateX = useSpring(x, springConfig);

  const handleMouseMove = (event) => {
    const targetRect = event.target.getBoundingClientRect();
    const eventOffsetX = event.clientX - targetRect.left;
    const offsetFromCenter = (eventOffsetX - targetRect.width / 2) / 2;
    x.set(offsetFromCenter);
  };

  return (
    <>
      {isMounted ? (
        <div className="hidden ">
          <img
            src={src}
            width={width}
            height={height}
            quality={quality}
            alt="hidden image"
          />
        </div>
      ) : null}

      <HoverCardPrimitive.Root
        openDelay={50}
        closeDelay={100}
        onOpenChange={(open) => {
          setOpen(open);
        }}
      >
        <HoverCardPrimitive.Trigger
          onMouseMove={handleMouseMove}
          className={className}
          href={url}
        >
          {children}
        </HoverCardPrimitive.Trigger>

        <HoverCardPrimitive.Content
          className="[transform-origin:var(--radix-hover-card-content-transform-origin)]"
          side="top"
          align="center"
          sideOffset={10}
          style={{ zIndex: 100 }}
        >
          <AnimatePresence>
            {isOpen && (
              <motion.div
                initial={{ opacity: 0, y: 20, scale: 0.6 }}
                animate={{
                  opacity: 1,
                  y: 0,
                  scale: 1,
                  transition: {
                    type: "spring",
                    stiffness: 260,
                    damping: 20,
                  },
                }}
                exit={{ opacity: 0, y: 20, scale: 0.6 }}
                className="shadow-xl rounded-xl "
                style={{
                  x: translateX,
                }}
              >
                <Link
                  to={url}
                  className="block p-1 bg-white border-2 border-transparent shadow rounded-xl hover:border-neutral-200 dark:hover:border-neutral-800"
                  style={{ fontSize: 0 }}
                >
                  <img
                    src={isStatic ? imageSrc : src}
                    width={width}
                    height={height}
                    quality={quality}
                    className="rounded-lg"
                    alt="preview image"
                  />
                </Link>
              </motion.div>
            )}
          </AnimatePresence>
        </HoverCardPrimitive.Content>
      </HoverCardPrimitive.Root>
    </>
  );
};`,
  props: [
    {
      name: "children",
      type: "ReactNode",
      default: "",
      description: "Content to display as the trigger for the link preview.",
    },
    {
      name: "url",
      type: "string",
      default: "",
      description: "URL to navigate to when clicking the link.",
    },
    {
      name: "className",
      type: "string",
      default: "",
      description: "Additional CSS classes to apply to the component.",
    },
    {
      name: "width",
      type: "number",
      default: "300",
      description: "Width of the preview image in pixels.",
    },
    {
      name: "height",
      type: "number",
      default: "225",
      description: "Height of the preview image in pixels.",
    },
    {
      name: "quality",
      type: "number",
      default: "50",
      description: "Quality of the preview image (1-100).",
    },
    {
      name: "isStatic",
      type: "boolean",
      default: "false",
      description:
        "Whether to use a static image instead of generating a screenshot.",
    },
    {
      name: "imageSrc",
      type: "string",
      default: "",
      description:
        "URL of the static image to use for the preview when isStatic is true.",
    },
  ],
  examples: [
    {
      title: "Basic usage",
      code: `<LinkPreview url="https://example.com">
  Hover over me
</LinkPreview>`,
    },
    {
      title: "With static image",
      code: `<LinkPreview 
  url="https://example.com" 
  isStatic={true} 
  imageSrc="/path/to/image.jpg"
>
  Hover to see preview
</LinkPreview>`,
    },
    {
      title: "Custom size",
      code: `<LinkPreview 
  url="https://example.com" 
  width={400} 
  height={300}
>
  Larger preview
</LinkPreview>`,
    },
  ],
};

export default component;
